<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;max-width:1300px;">
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <h4 class="st-page-title"><i class="mdi mdi-cash-register me-2"></i>School Fees</h4>
      </div>
      <div class="d-flex gap-2 flex-wrap align-items-end">
        <select class="st-input" v-model="termId" @change="loadDashboard" style="width:180px;padding:7px 10px;">
          <option value="">Select Term</option>
          <option v-for="t in terms" :key="t.id" :value="t.id">{{ t.name }} {{ t.is_current == 1 ? '(current)' : '' }}</option>
        </select>
        <button class="btn st-btn-outline btn-sm" @click="exportPdf">📄 PDF</button>
        <button class="btn st-btn-primary btn-sm" @click="loadDashboard">🔄</button>
      </div>
    </div>

    <!-- Summary -->
    <div class="fee-grid" v-if="summary">
      <div class="fee-card"><div class="fee-label">Total Billed</div><div class="fee-value">KSh {{ fmt(summary.total_billed) }}</div><div class="fee-sub">{{ summary.total_students || 0 }} students</div></div>
      <div class="fee-card"><div class="fee-label">Collected</div><div class="fee-value" style="color:#10B981;">KSh {{ fmt(summary.total_collected) }}</div><div class="fee-sub">{{ collectionRate }}% collected</div></div>
      <div class="fee-card"><div class="fee-label">Outstanding</div><div class="fee-value" style="color:#EF4444;">KSh {{ fmt(summary.total_outstanding) }}</div><div class="fee-sub">{{ summary.unpaid || 0 }} unpaid</div></div>
      <div class="fee-card"><div class="fee-label">Scholarships</div><div class="fee-value" style="color:#8B5CF6;">KSh {{ fmt(summary.total_scholarships) }}</div><div class="fee-sub">{{ summary.fully_paid || 0 }} fully paid</div></div>
    </div>

    <!-- Tabs -->
    <div class="la-tabs">
      <button class="la-tab" :class="{active:tab==='overview'}" @click="tab='overview'">📊 Overview</button>
      <button class="la-tab" :class="{active:tab==='structure'}" @click="tab='structure'; loadStructure()">📋 Fee Structure</button>
      <button class="la-tab" :class="{active:tab==='students'}" @click="tab='students'; loadDefaulters()">👥 Students</button>
      <button class="la-tab" :class="{active:tab==='terms'}" @click="tab='terms'">📅 Terms</button>
    </div>

    <!-- OVERVIEW -->
    <div v-if="tab==='overview'">
      <div class="row g-3">
        <div class="col-md-7">
          <div class="st-section" style="padding:14px;">
            <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">📊 Collection by Class</div>
            <div v-for="c in byClass" :key="c.class_name" style="margin-bottom:10px;">
              <div class="d-flex justify-content-between" style="font-size:12px;margin-bottom:2px;">
                <span style="color:#E8EDF4;">{{ c.class_name || 'No class' }} <span style="color:#4A6A8A;">({{ c.students }})</span></span>
                <span><span style="color:#10B981;">KSh {{ fmt(c.collected) }}</span> / <span style="color:#8BA4C4;">{{ fmt(c.billed) }}</span></span>
              </div>
              <div style="background:#1A3A6A;border-radius:4px;height:8px;overflow:hidden;">
                <div :style="{width:(c.collection_rate||0)+'%', background: (c.collection_rate||0)>=80?'#10B981':(c.collection_rate||0)>=50?'#F59E0B':'#EF4444', height:'100%', borderRadius:'4px'}"></div>
              </div>
              <div style="font-size:10px;color:#4A6A8A;text-align:right;">{{ c.collection_rate||0 }}% · KSh {{ fmt(c.outstanding) }} outstanding</div>
            </div>
            <div v-if="!byClass.length" style="color:#4A6A8A;text-align:center;padding:20px;">No bills generated yet</div>
          </div>
        </div>
        <div class="col-md-5">
          <div class="st-section" style="padding:14px;">
            <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">💰 Recent Payments</div>
            <div v-for="p in recentPayments" :key="p.completed_at" style="padding:6px 0;border-bottom:1px solid #1A3A6A;font-size:12px;">
              <div class="d-flex justify-content-between"><strong style="color:#E8EDF4;">{{ p.student_name }}</strong><span style="color:#10B981;font-weight:600;">KSh {{ fmt(p.amount) }}</span></div>
              <div style="color:#4A6A8A;font-size:10px;">{{ p.admission_no }} · {{ p.provider_ref || 'Cash' }} · {{ (p.completed_at||'').substring(0,16) }}</div>
            </div>
            <div v-if="!recentPayments.length" style="color:#4A6A8A;text-align:center;padding:20px;">No payments yet</div>
          </div>
        </div>
      </div>
    </div>

    <!-- FEE STRUCTURE -->
    <div v-if="tab==='structure'">
      <div class="st-section" style="padding:14px;">
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div class="d-flex gap-2 align-items-end">
            <div><label class="st-label" style="font-size:10px;">Class</label><select class="st-input" v-model="strGrade" @change="loadStructure" style="width:160px;padding:8px;"><option value="">All classes</option><option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option></select></div>
          </div>
          <button class="btn st-btn-primary btn-sm" @click="showAddFee=true">+ Add Fee Item</button>
        </div>
        <div v-if="feeItems.length">
          <div class="d-flex justify-content-between mb-2" style="font-size:13px;"><span style="color:#8BA4C4;">{{ feeItems.length }} items</span><span style="font-weight:700;color:#E8EDF4;">Total: KSh {{ fmt(feeTotal) }} <span style="color:#4A6A8A;font-weight:400;">(Mandatory: KSh {{ fmt(feeMandatory) }})</span></span></div>
          <table class="la-table">
            <thead><tr><th>Category</th><th>Amount</th><th>Class</th><th>Term</th><th>Type</th><th></th></tr></thead>
            <tbody>
              <tr v-for="f in feeItems" :key="f.id">
                <td><strong style="color:#E8EDF4;">{{ f.category }}</strong></td>
                <td style="color:#10B981;font-weight:600;">KSh {{ fmt(f.amount) }}</td>
                <td>{{ f.grade_name || 'All' }}</td>
                <td>{{ f.term_name || 'All' }}</td>
                <td><span :style="{color: f.is_optional==1 ? '#F59E0B' : '#10B981'}">{{ f.is_optional==1 ? 'Optional' : 'Mandatory' }}</span></td>
                <td><button class="btn st-btn-danger btn-sm" style="padding:2px 8px;font-size:10px;" @click="deleteFeeItem(f)">🗑</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else style="text-align:center;padding:20px;color:#4A6A8A;">No fee items. Add some to define the fee structure.</div>

        <div class="d-flex gap-2 mt-3 flex-wrap">
          <button class="btn st-btn-primary btn-sm" @click="generateBills" :disabled="generating">
            <span v-if="generating" class="spinner-border spinner-border-sm me-1"></span>⚡ Generate Bills for {{ strGrade ? 'this class' : 'all students' }}
          </button>
        </div>
        <div v-if="genMsg" class="st-alert mt-2" :class="genOk ? 'st-alert-success' : 'st-alert-danger'">{{ genMsg }}</div>
      </div>
    </div>

    <!-- STUDENTS -->
    <div v-if="tab==='students'">
      <div class="st-section" style="padding:14px;">
        <div class="d-flex gap-2 flex-wrap align-items-end mb-3">
          <div><label class="st-label" style="font-size:10px;">Class</label><select class="st-input" v-model="defClass" @change="loadDefaulters" style="width:160px;padding:8px;"><option value="">All</option><option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option></select></div>
          <div><label class="st-label" style="font-size:10px;">Search</label><input class="st-input" v-model="defSearch" placeholder="Name or admission..." style="width:160px;" @keyup.enter="loadDefaulters" /></div>
          <button class="btn st-btn-primary btn-sm" @click="loadDefaulters">🔍</button>
        </div>
        <table v-if="defaulters.length" class="la-table">
          <thead><tr><th>Student</th><th>Admission</th><th>Class</th><th>Billed</th><th>Paid</th><th>Outstanding</th><th></th></tr></thead>
          <tbody>
            <tr v-for="d in defaulters" :key="d.name+d.admission_no" :style="{background: d.outstanding > 0 ? '' : 'rgba(16,185,129,.03)'}">
              <td><strong style="color:#E8EDF4;">{{ d.name }}</strong></td>
              <td>{{ d.admission_no }}</td>
              <td>{{ d.class_name }}</td>
              <td>KSh {{ fmt(d.total_fees) }}</td>
              <td style="color:#10B981;">KSh {{ fmt(d.total_paid) }}</td>
              <td :style="{color: d.outstanding > 0 ? '#EF4444' : '#10B981', fontWeight:600}">KSh {{ fmt(d.outstanding) }}</td>
              <td>
                <button class="btn st-btn-primary btn-sm" style="padding:2px 8px;font-size:10px;" @click="openPayModal(d)">💰</button>
                <button class="btn st-btn-outline btn-sm" style="padding:2px 8px;font-size:10px;" @click="openStatement(d)">📄</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else style="text-align:center;padding:20px;color:#4A6A8A;">No fee records. Generate bills first.</div>
      </div>
    </div>

    <!-- TERMS -->
    <div v-if="tab==='terms'">
      <div class="st-section" style="padding:14px;">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div style="font-size:13px;font-weight:600;color:#4FC3F7;">📅 Academic Terms</div>
          <button class="btn st-btn-primary btn-sm" @click="termForm={name:'',startDate:'',endDate:'',isCurrent:false};showTermModal=true">+ Add Term</button>
        </div>
        <div v-for="t in terms" :key="t.id" class="tt-subject-row">
          <div>
            <strong style="color:#E8EDF4;">{{ t.name }}</strong>
            <span v-if="t.is_current==1" style="background:rgba(16,185,129,.15);color:#10B981;padding:2px 8px;border-radius:4px;font-size:10px;margin-left:8px;">CURRENT</span>
            <div style="font-size:11px;color:#4A6A8A;">{{ t.start_date }} to {{ t.end_date }}</div>
          </div>
          <button v-if="t.is_current!=1" class="btn st-btn-outline btn-sm" style="padding:3px 10px;font-size:11px;" @click="setCurrentTerm(t)">Set Current</button>
        </div>
        <div v-if="!terms.length" style="text-align:center;padding:20px;color:#4A6A8A;">No terms. Create one to start billing.</div>
      </div>
    </div>

    <!-- ADD FEE MODAL -->
    <div v-if="showAddFee" class="tt-overlay" @click.self="showAddFee=false"><div class="tt-modal" style="max-width:420px;">
      <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">+ Add Fee Item</h5>
      <div class="mb-3"><label class="st-label">Category *</label><input class="st-input" v-model="feeForm.category" placeholder="e.g. Tuition, Boarding, Activity" /></div>
      <div class="mb-3"><label class="st-label">Amount (KSh) *</label><input class="st-input" type="number" v-model.number="feeForm.amount" /></div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Class</label><select class="st-input" v-model="feeForm.gradeId"><option value="">All classes</option><option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option></select></div>
        <div class="col-6"><label class="st-label">Term</label><select class="st-input" v-model="feeForm.termId"><option value="">All terms</option><option v-for="t in terms" :key="t.id" :value="t.id">{{ t.name }}</option></select></div>
      </div>
      <label style="font-size:12px;color:#8BA4C4;display:flex;align-items:center;gap:6px;margin-bottom:12px;"><input type="checkbox" v-model="feeForm.isOptional" /> Optional fee</label>
      <div class="d-flex gap-2 justify-content-end">
        <button class="btn st-btn-outline btn-sm" @click="showAddFee=false">Cancel</button>
        <button class="btn st-btn-primary btn-sm" @click="addFeeItem" :disabled="feeSaving">Save</button>
      </div>
    </div></div>

    <!-- RECORD PAYMENT MODAL -->
    <div v-if="payModal" class="tt-overlay" @click.self="payModal=null"><div class="tt-modal" style="max-width:420px;">
      <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">💰 Record Fee Payment</h5>
      <div class="st-info-box mb-3"><strong style="color:#E8EDF4;">{{ payModal.name }}</strong> <span style="color:#4A6A8A;">{{ payModal.admission_no }}</span><div style="color:#EF4444;font-size:13px;margin-top:4px;">Outstanding: KSh {{ fmt(payModal.outstanding) }}</div></div>
      <div class="mb-3"><label class="st-label">Amount (KSh)</label><input class="st-input" type="number" v-model.number="payForm.amount" :max="payModal.outstanding" /></div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Method</label><select class="st-input" v-model="payForm.method"><option value="cash">Cash</option><option value="bank_transfer">Bank</option><option value="cheque">Cheque</option><option value="mpesa_c2b">M-Pesa</option></select></div>
        <div class="col-6"><label class="st-label">Ref / Receipt</label><input class="st-input" v-model="payForm.ref" placeholder="Optional" /></div>
      </div>
      <div class="d-flex gap-2 justify-content-end">
        <button class="btn st-btn-outline btn-sm" @click="payModal=null">Cancel</button>
        <button class="btn st-btn-primary btn-sm" @click="recordPayment" :disabled="payLoading"><span v-if="payLoading" class="spinner-border spinner-border-sm me-1"></span>Record</button>
      </div>
      <div v-if="payMsg" class="st-alert mt-2" :class="payOk?'st-alert-success':'st-alert-danger'">{{ payMsg }}</div>
    </div></div>

    <!-- TERM MODAL -->
    <div v-if="showTermModal" class="tt-overlay" @click.self="showTermModal=false"><div class="tt-modal" style="max-width:420px;">
      <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">📅 Add Term</h5>
      <div class="mb-3"><label class="st-label">Name</label><input class="st-input" v-model="termForm.name" placeholder="e.g. Term 1 2026" /></div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Start Date</label><input class="st-input" type="date" v-model="termForm.startDate" /></div>
        <div class="col-6"><label class="st-label">End Date</label><input class="st-input" type="date" v-model="termForm.endDate" /></div>
      </div>
      <label style="font-size:12px;color:#8BA4C4;display:flex;align-items:center;gap:6px;margin-bottom:12px;"><input type="checkbox" v-model="termForm.isCurrent" /> Set as current term</label>
      <div class="d-flex gap-2 justify-content-end">
        <button class="btn st-btn-outline btn-sm" @click="showTermModal=false">Cancel</button>
        <button class="btn st-btn-primary btn-sm" @click="addTerm" :disabled="termSaving">Save</button>
      </div>
    </div></div>

    <!-- STATEMENT MODAL -->
    <div v-if="stmtModal" class="tt-overlay" @click.self="stmtModal=null"><div class="tt-modal" style="max-width:550px;">
      <div class="d-flex justify-content-between mb-3"><h5 style="color:#E8EDF4;margin:0;font-size:15px;">📄 Fee Statement</h5><button class="btn" style="color:#8BA4C4;font-size:18px;padding:0;" @click="stmtModal=null">✕</button></div>
      <div v-if="stmtLoading" class="st-spinner"><span class="spinner-border"></span></div>
      <div v-else-if="stmt">
        <div class="st-info-box mb-3"><strong style="color:#E8EDF4;font-size:16px;">{{ stmt.student?.name }}</strong><div style="color:#4A6A8A;font-size:12px;">{{ stmt.student?.admission_no }} · {{ stmt.student?.class_name }}</div></div>
        <div v-for="b in stmt.balances" :key="b.id" style="padding:8px 0;border-bottom:1px solid #1A3A6A;font-size:12px;">
          <div class="d-flex justify-content-between"><strong style="color:#4FC3F7;">{{ b.term_name }}</strong><span :style="{color:b.outstanding>0?'#EF4444':'#10B981',fontWeight:600}">{{ b.outstanding > 0 ? 'KSh '+fmt(b.outstanding)+' due' : '✅ Paid' }}</span></div>
          <div style="color:#8BA4C4;">Billed: KSh {{ fmt(b.total_fees) }} · Paid: KSh {{ fmt(b.total_paid) }} · Scholarship: KSh {{ fmt(b.scholarship_amount) }}</div>
        </div>
        <div v-if="stmt.payments?.length" style="margin-top:12px;">
          <div style="font-size:12px;font-weight:600;color:#10B981;margin-bottom:6px;">Payment History</div>
          <div v-for="p in stmt.payments" :key="p.id" style="font-size:11px;padding:4px 0;border-bottom:1px solid #1A3A6A;color:#8BA4C4;">
            {{ (p.completed_at||p.created_at||'').substring(0,16) }} · KSh {{ fmt(p.amount) }} · {{ p.description || p.mpesa_code || '' }}
          </div>
        </div>
      </div>
    </div></div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'FeesDashboard',
  components: { Navigation },
  data() { return {
    tab:'overview', termId:'', terms:[], classes:[], summary:null, collectionRate:0,
    byClass:[], recentPayments:[],
    // Structure
    strGrade:'', feeItems:[], feeTotal:0, feeMandatory:0,
    showAddFee:false, feeSaving:false,
    feeForm:{category:'',amount:0,gradeId:'',termId:'',isOptional:false},
    generating:false, genMsg:'', genOk:false,
    // Students
    defaulters:[], defClass:'', defSearch:'',
    payModal:null, payForm:{amount:0,method:'cash',ref:''}, payLoading:false, payMsg:'', payOk:false,
    // Statement
    stmtModal:null, stmt:null, stmtLoading:false,
    // Terms
    showTermModal:false, termSaving:false,
    termForm:{name:'',startDate:'',endDate:'',isCurrent:false},
  }; },
  mounted() { this.loadDashboard(); this.loadClasses(); },
  methods: {
    fmt(v) { return Number(v||0).toLocaleString(); },
    async loadClasses() { try{const r=await executeJson({function:'getClasses'},simitrack);if(r.data.success)this.classes=r.data.data||[];}catch(e){} },
    async loadDashboard() {
      try {
        const r = await executeJson({function:'getFeesDashboard', termId:this.termId}, simitrack);
        if (r.data.success) {
          const d = r.data.data;
          this.summary = d.summary; this.collectionRate = d.collection_rate;
          this.byClass = d.by_class||[]; this.recentPayments = d.recent_payments||[];
          this.terms = d.terms||[];
          if (!this.termId && d.term_id) this.termId = d.term_id;
        }
      } catch(e) {}
    },
    async loadStructure() {
      try {
        const r = await executeJson({function:'getFeeStructure', termId:this.termId, gradeId:this.strGrade}, simitrack);
        if (r.data.success) { this.feeItems=r.data.data?.items||[]; this.feeTotal=r.data.data?.total||0; this.feeMandatory=r.data.data?.mandatory||0; }
      } catch(e) {}
    },
    async addFeeItem() {
      if (!this.feeForm.category||!this.feeForm.amount) return;
      this.feeSaving=true;
      try {
        const r = await executeJson({function:'addFeeStructure',category:this.feeForm.category,amount:this.feeForm.amount,gradeId:this.feeForm.gradeId,termId:this.feeForm.termId||this.termId,isOptional:this.feeForm.isOptional?1:0},simitrack);
        if (r.data.success) { this.showAddFee=false; this.loadStructure(); }
      } catch(e) {}
      this.feeSaving=false;
    },
    async deleteFeeItem(f) { if(!confirm('Delete "'+f.category+'"?'))return; try{await executeJson({function:'deleteFeeItem',id:f.id},simitrack);this.loadStructure();}catch(e){} },
    async generateBills() {
      if (!this.termId) { this.genMsg='Select a term first'; this.genOk=false; return; }
      if (!confirm('Generate fee bills for '+(this.strGrade?'this class':'ALL students')+'?')) return;
      this.generating=true; this.genMsg='';
      try {
        const r = await executeJson({function:'generateBills',termId:this.termId,gradeId:this.strGrade},simitrack);
        this.genMsg=r.data.message; this.genOk=r.data.success;
        if (r.data.success) this.loadDashboard();
      } catch(e) { this.genMsg='Error'; }
      this.generating=false;
    },
    async loadDefaulters() {
      try {
        const r = await executeJson({function:'getFeeDefaulters',termId:this.termId,classId:this.defClass,minOutstanding:0},simitrack);
        if (r.data.success) this.defaulters=r.data.data||[];
      } catch(e) {}
    },
    openPayModal(d) { this.payModal=d; this.payForm={amount:parseFloat(d.outstanding)||0,method:'cash',ref:''}; this.payMsg=''; },
    async recordPayment() {
      if (!this.payForm.amount||this.payForm.amount<=0) return;
      this.payLoading=true; this.payMsg='';
      try {
        // Need student ID — lookup by admission
        const lookup = await executeJson({function:'searchStudentsForMatch',query:this.payModal.admission_no},simitrack);
        const student = (lookup.data.data||[])[0];
        if (!student) { this.payMsg='Student not found'; this.payOk=false; this.payLoading=false; return; }
        const r = await executeJson({function:'recordManualFeePayment',studentId:student.id,amount:this.payForm.amount,method:this.payForm.method,ref:this.payForm.ref},simitrack);
        this.payMsg=r.data.message; this.payOk=r.data.success;
        if (r.data.success) { setTimeout(()=>{this.payModal=null;this.loadDashboard();this.loadDefaulters();},1200); }
      } catch(e) { this.payMsg='Error'; }
      this.payLoading=false;
    },
    async openStatement(d) {
      this.stmtModal=d; this.stmt=null; this.stmtLoading=true;
      try {
        const lookup = await executeJson({function:'searchStudentsForMatch',query:d.admission_no},simitrack);
        const student = (lookup.data.data||[])[0];
        if (student) {
          const r = await executeJson({function:'getStudentFeeStatement',studentId:student.id},simitrack);
          if (r.data.success) this.stmt=r.data.data;
        }
      } catch(e) {}
      this.stmtLoading=false;
    },
    async addTerm() {
      if (!this.termForm.name||!this.termForm.startDate||!this.termForm.endDate) return;
      this.termSaving=true;
      try {
        const r = await executeJson({function:'addTerm',name:this.termForm.name,startDate:this.termForm.startDate,endDate:this.termForm.endDate,isCurrent:this.termForm.isCurrent?1:0},simitrack);
        if (r.data.success) { this.showTermModal=false; this.loadDashboard(); }
      } catch(e) {}
      this.termSaving=false;
    },
    async setCurrentTerm(t) {
      try { await executeJson({function:'addTerm',name:t.name,startDate:t.start_date,endDate:t.end_date,isCurrent:1},simitrack); this.loadDashboard(); } catch(e) {}
    },
    exportPdf() {
      const inst = sessionStorage.getItem('institutionName')||'';
      const s = this.summary||{};
      let html = `<!DOCTYPE html><html><head><title>Fee Report</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;padding:15mm}@page{size:A4;margin:12mm}h1{font-size:18px;color:#0D2B5E}h2{font-size:14px;color:#2E86DE;margin:4px 0 12px}.summary{display:flex;gap:12px;margin-bottom:16px}.sbox{flex:1;background:#f0f7ff;border:1px solid #d0e3f7;border-radius:6px;padding:10px;text-align:center}.sbox .val{font-size:18px;font-weight:800;color:#0D2B5E}.sbox .lbl{font-size:9px;color:#666;text-transform:uppercase}table{width:100%;border-collapse:collapse;margin-top:8px}th{background:#0D2B5E;color:#fff;padding:5px 8px;font-size:10px;text-align:left}td{padding:4px 8px;border-bottom:1px solid #e0e0e0;font-size:10px}tr:nth-child(even){background:#f9fbfd}.footer{margin-top:16px;border-top:1px solid #ddd;padding-top:8px;font-size:9px;color:#999}</style></head><body>`;
      html += `<h1>${inst}</h1><h2>Fee Collection Report</h2>`;
      html += `<div class="summary"><div class="sbox"><div class="val">KSh ${this.fmt(s.total_billed)}</div><div class="lbl">Billed</div></div><div class="sbox"><div class="val">KSh ${this.fmt(s.total_collected)}</div><div class="lbl">Collected</div></div><div class="sbox"><div class="val">KSh ${this.fmt(s.total_outstanding)}</div><div class="lbl">Outstanding</div></div><div class="sbox"><div class="val">${this.collectionRate}%</div><div class="lbl">Rate</div></div></div>`;
      if (this.defaulters.length) {
        html += '<table><tr><th>Student</th><th>Admission</th><th>Class</th><th>Billed</th><th>Paid</th><th>Outstanding</th></tr>';
        this.defaulters.forEach(d => { html += `<tr><td>${d.name}</td><td>${d.admission_no}</td><td>${d.class_name||''}</td><td>KSh ${this.fmt(d.total_fees)}</td><td>KSh ${this.fmt(d.total_paid)}</td><td style="color:${d.outstanding>0?'#dc2626':'#059669'};font-weight:600">KSh ${this.fmt(d.outstanding)}</td></tr>`; });
        html += '</table>';
      }
      html += `<div class="footer">${inst} — Simi Trac · Generated ${new Date().toLocaleString()}</div></body></html>`;
      const w = window.open('','_blank'); w.document.write(html); w.document.close(); setTimeout(()=>w.print(),600);
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;}.st-back:hover{color:#4FC3F7;}.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:20px;margin-bottom:16px;}.st-label{color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;display:block;}
.st-input{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:13px;outline:none;}.st-input:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline:hover{border-color:#4FC3F7;color:#4FC3F7;}
.st-btn-danger{background:rgba(239,68,68,.15);border:1px solid #EF4444;color:#EF4444;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;}.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}.st-alert-danger{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);color:#EF4444;}
.st-info-box{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;}.st-spinner{display:flex;justify-content:center;padding:30px;}.st-spinner .spinner-border{color:#2E86DE;}
.fee-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px;margin-bottom:16px;}.fee-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:10px;padding:14px;}.fee-label{font-size:10px;color:#4A6A8A;text-transform:uppercase;font-weight:600;}.fee-value{font-size:20px;font-weight:800;margin:2px 0;color:#E8EDF4;}.fee-sub{font-size:11px;color:#8BA4C4;}
.la-tabs{display:flex;gap:4px;margin-bottom:16px;border-bottom:2px solid #1A3A6A;}.la-tab{background:none;border:none;padding:10px 16px;color:#4A6A8A;font-size:13px;font-weight:600;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px;}.la-tab.active{color:#4FC3F7;border-bottom-color:#4FC3F7;}
.la-table{width:100%;border-collapse:collapse;font-size:12px;color:#E8EDF4;}.la-table th{background:#0A1628;padding:6px 8px;color:#4FC3F7;font-size:10px;text-transform:uppercase;border-bottom:2px solid #1A3A6A;}.la-table td{padding:5px 8px;border-bottom:1px solid #1A3A6A;}
.tt-subject-row{display:flex;justify-content:space-between;align-items:center;padding:10px 12px;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;margin-bottom:6px;}
.tt-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;z-index:999;}.tt-modal{background:#0D2B5E;border-radius:14px;padding:24px;width:95%;max-width:520px;max-height:90vh;overflow-y:auto;border:1px solid #1A3A6A;}
</style>
